// frontend-react/src/components/RuleManagement/RuleSelector.tsx
import React, { useState } from 'react';
import {
  Card,
  Select,
  Button,
  Space,
  Popconfirm,
  Tooltip,
  message,
  Tag,
  Modal,
  Input,
  Spin,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  TeamOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  StarFilled,
  StarOutlined,
  DownOutlined,
  RightOutlined,
  SettingOutlined,
  ExperimentOutlined,
  DatabaseOutlined,
  BookOutlined,
} from '@ant-design/icons';
import type { DefaultOptionType } from 'antd/es/select';

import { RuleSet } from '../../types/rule';
import {
  deleteRuleSet,
  setDefaultRuleSet,
  unsetDefaultRuleSet,
  toggleContentLock,
  getCompanyRiskLevels,
  updateCompanyRiskLevels,
  getCompanyComparisonRiskLevels,
  updateCompanyComparisonRiskLevels,
} from '../../services/ruleService';
import LegaltechRulesModal from './LegaltechRulesModal';

interface RuleSelectorProps {
  ruleSets: RuleSet[];
  manageRuleSets?: RuleSet[] | null; // ✅ 管理區塊用（null=不顯示）
  selectedRuleSetId: number | null;
  onRuleSetChange: (ruleSetId: number | null) => void;
  // AI 生成規則用：覆蓋 selectedRuleSetId / onRuleSetChange
  selectedValue?: string | number | null;
  onValueChange?: (val: string | number | null | undefined) => void;
  onUploadClick: () => void;
  onShareClick: (ruleSet: RuleSet) => void;
  onPreviewClick: (ruleSet: RuleSet) => void;
  onRefresh: () => void;
  isLoading: boolean;
  canUploadRules?: boolean | undefined;
  canShareRules?: boolean | undefined;
  isAdmin?: boolean | undefined;
  isSuperAdmin?: boolean | undefined;
  userId?: string | number | undefined;
  showAiOption?: boolean;
  /** 比對頁面用：隱藏 AI 選項、驗測模式等審閱專用功能 */
  isComparisonPage?: boolean;
  compact?: boolean;
  canUseValidationMode?: boolean;
  validationMode?: boolean;
  onValidationModeToggle?: (value: boolean) => void;
  userPreferredRuleSetId?: number | null;
  onSetUserPreferred?: (id: number | null) => void;
  historyRuleSetName?: string | null;
}

const RuleSelector: React.FC<RuleSelectorProps> = ({
  ruleSets,
  manageRuleSets = null,
  selectedRuleSetId,
  onRuleSetChange,
  selectedValue,
  onValueChange,
  onUploadClick,
  onShareClick,
  onPreviewClick,
  onRefresh,
  isLoading,
  canUploadRules = false,
  canShareRules = false,
  isAdmin = false,
  isSuperAdmin = false,
  userId,
  showAiOption = false,
  isComparisonPage = false,
  compact = false,
  canUseValidationMode = false,
  validationMode = false,
  onValidationModeToggle,
  userPreferredRuleSetId = null,
  onSetUserPreferred,
  historyRuleSetName,
}) => {
  const [isManageListExpanded, setIsManageListExpanded] = useState(false);

  // 公司風險等級設定 Modal
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [legaltechModalOpen, setLegaltechModalOpen] = useState(false);
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskSaving, setRiskSaving] = useState(false);
  const [riskHigh, setRiskHigh] = useState('');
  const [riskMedium, setRiskMedium] = useState('');
  const [riskLow, setRiskLow] = useState('');

  const openRiskModal = async () => {
    setRiskModalOpen(true);
    setRiskLoading(true);
    const defs = isComparisonPage
      ? await getCompanyComparisonRiskLevels()
      : await getCompanyRiskLevels();
    setRiskHigh(defs.high || '');
    setRiskMedium(defs.medium || '');
    setRiskLow(defs.low || '');
    setRiskLoading(false);
  };

  const handleSaveRiskLevels = async () => {
    setRiskSaving(true);
    const config: Record<string, string> = {};
    if (riskHigh.trim()) config.high = riskHigh.trim();
    if (riskMedium.trim()) config.medium = riskMedium.trim();
    if (riskLow.trim()) config.low = riskLow.trim();
    const payload = Object.keys(config).length > 0 ? config : null;
    const result = isComparisonPage
      ? await updateCompanyComparisonRiskLevels(payload)
      : await updateCompanyRiskLevels(payload);
    setRiskSaving(false);
    if (result.success) {
      message.success('公司預設風險等級已更新');
      setRiskModalOpen(false);
    } else {
      message.error(`儲存失敗: ${result.error}`);
    }
  };

  const canDeleteRule = (ruleSet: RuleSet) => {
    if (ruleSet.is_legaltech_library) return isSuperAdmin;
    if (isAdmin) return true;
    return ruleSet.created_by === Number(userId);
  };

  const handleDeleteRule = async (ruleSetId: number, ruleSetName: string) => {
    const result = await deleteRuleSet(ruleSetId);

    if (result.success) {
      message.success(`規則集「${ruleSetName}」已刪除`);
      if (selectedRuleSetId === ruleSetId) {
        onRuleSetChange(null);
      }
      onRefresh();
    } else {
      message.error(`刪除失敗: ${result.error}`);
    }
  };

  const handleSetDefault = async (ruleSet: RuleSet) => {
    const result = await setDefaultRuleSet(ruleSet.id);
    if (result.success) {
      message.success(`已將「${ruleSet.rule_set_name}」設為預設規則`);
      onRefresh(); // ✅ 建議保留：確保 default 標記與可見性同步
    } else {
      message.error(`設為預設失敗: ${result.error}`);
    }
  };

  const handleUnsetDefault = async (ruleSet: RuleSet) => {
    const result = await unsetDefaultRuleSet(ruleSet.id);
    if (result.success) {
      message.success(`已取消「${ruleSet.rule_set_name}」的預設設定`);
      onRefresh();
    } else {
      message.error(`取消預設失敗: ${result.error}`);
    }
  };

  const handleToggleLock = async (ruleSet: RuleSet) => {
    const result = await toggleContentLock(ruleSet.id);
    if (result.success) {
      const statusText = result.content_locked ? '已鎖定' : '已解鎖';
      message.success(`「${ruleSet.rule_set_name}」內容${statusText}`);
      onRefresh();
    } else {
      message.error(`操作失敗: ${result.error}`);
    }
  };

  // 清單資料：有管理權限用 manageRuleSets，沒有權限則 fallback 到 ruleSets
  // 律果規則庫：下拉選單可見，但不出現在「已上傳的規則集」清單
  // Super Admin 透過「律果規則」按鈕另行管理
  // 無「上傳規則」權限的使用者看不到任何規則內容，故不顯示「已上傳的規則集」區塊
  const filteredRuleSets = ruleSets.filter(r => !!r.is_comparison === isComparisonPage);
  const listRuleSets = (manageRuleSets ?? ruleSets)
    .filter(r => !r.is_legaltech_library)
    .filter(r => !!r.is_comparison === isComparisonPage);
  const visibleRuleSets = filteredRuleSets;
  const showManageBlock = canUploadRules && listRuleSets.length > 0;

  // 公司預設規則集（用於下拉選單置頂）
  const companyDefaultRuleSet =
    visibleRuleSets.find(r => r.is_default && !r.is_legaltech_library) ?? null;

  //   console.log('[RuleSelector render]', {
  //   ruleSetsLen: ruleSets?.length,
  //   manageRuleSets,
  //   selectedRuleSetId,
  //   isLoading,
  //   canUploadRules,
  //   isAdmin,
  //   userId,
  // });

  // 當 value 是數字但在選項中找不到對應項目時（例如規則集尚未載入完成），
  // 顯示 null 讓 placeholder 出現，而非顯示原始數字 ID
  const rawValue = (selectedValue !== undefined ? selectedValue : selectedRuleSetId) ?? null;
  const resolvedValue = (() => {
    if (rawValue === null || rawValue === 'ai_generated') return rawValue;
    if (typeof rawValue === 'number') {
      const exists =
        visibleRuleSets.some(r => r.id === rawValue) ||
        (companyDefaultRuleSet && companyDefaultRuleSet.id === rawValue) ||
        (userPreferredRuleSetId !== null && userPreferredRuleSetId === rawValue);
      return exists ? rawValue : null;
    }
    return rawValue;
  })();

  const ruleSelect = (
    <Select<string | number | null, DefaultOptionType>
      style={compact ? { minWidth: 280 } : { width: 420 }}
      size={compact ? 'small' : 'middle'}
      placeholder={compact ? '請選擇審閱規則' : '選擇規則集（不選則使用預設規則）'}
      allowClear
      loading={isLoading}
      value={resolvedValue}
      onChange={val => {
        if (onValueChange) onValueChange(val ?? null);
        else onRuleSetChange((val as number | null) ?? null);
      }}
    >
      {/* 個人偏好置頂：有設偏好時，在最上方顯示（-1=AI 模式，>0=一般規則集） */}
      {(() => {
        if (userPreferredRuleSetId === null || userPreferredRuleSetId === undefined) return null;
        // AI 模式偏好
        if (userPreferredRuleSetId === -1 && showAiOption) {
          return (
            <>
              <Select.Option key='preferred-pinned-ai' value='ai_generated'>
                <StarFilled style={{ color: '#1677ff', marginRight: 6 }} />✨ AI 自動審閱
                <span style={{ color: '#1677ff', marginLeft: 8, fontSize: 12 }}>我的偏好</span>
              </Select.Option>
              <Select.Option
                key='preferred-divider'
                value='__divider__'
                disabled
                style={{ padding: 0, minHeight: 0 }}
              >
                <div style={{ borderTop: '1px solid #f0f0f0', margin: '2px -12px' }} />
              </Select.Option>
            </>
          );
        }
        // 一般規則集偏好
        const preferred = visibleRuleSets.find(r => r.id === userPreferredRuleSetId);
        if (!preferred) return null;
        return (
          <>
            <Select.Option key={`preferred-pinned-${preferred.id}`} value={preferred.id}>
              <StarFilled style={{ color: '#1677ff', marginRight: 6 }} />
              {preferred.rule_set_name}
              <span style={{ color: '#1677ff', marginLeft: 8, fontSize: 12 }}>我的偏好</span>
            </Select.Option>
            <Select.Option
              key='preferred-divider'
              value='__divider__'
              disabled
              style={{ padding: 0, minHeight: 0 }}
            >
              <div style={{ borderTop: '1px solid #f0f0f0', margin: '2px -12px' }} />
            </Select.Option>
          </>
        );
      })()}
      {/* 公司預設置頂：若不同於個人偏好則在第二位顯示 */}
      {companyDefaultRuleSet &&
        companyDefaultRuleSet.id !== userPreferredRuleSetId &&
        (() => {
          const r = companyDefaultRuleSet;
          const uploaderInfo =
            !compact && r.created_by_name
              ? `${r.created_by_name}${r.created_by_department ? ` (${r.created_by_department})` : ''}`
              : '';
          return (
            <>
              <Select.Option key={`company-default-pinned-${r.id}`} value={r.id}>
                <StarFilled style={{ color: '#faad14', marginRight: 6 }} />
                {r.rule_set_name}
                {!compact && (
                  <span style={{ color: '#999', marginLeft: 8 }}>({r.rules_count} 條規則)</span>
                )}
                <span style={{ color: '#52c41a', marginLeft: 8, fontSize: 12 }}>預設</span>
                {!compact && uploaderInfo && (
                  <span style={{ color: '#1E88A8', marginLeft: 8, fontSize: 12 }}>
                    - {uploaderInfo}
                  </span>
                )}
              </Select.Option>
              <Select.Option
                key='company-default-divider'
                value='__company_default_divider__'
                disabled
                style={{ padding: 0, minHeight: 0 }}
              >
                <div style={{ borderTop: '1px solid #f0f0f0', margin: '2px -12px' }} />
              </Select.Option>
            </>
          );
        })()}
      {/* AI 選項：若已置頂為個人偏好則不重複顯示 */}
      {showAiOption && userPreferredRuleSetId !== -1 && (
        <Select.Option key='ai_generated' value='ai_generated'>
          ✨ AI 自動審閱
        </Select.Option>
      )}
      {/* 規則集列表：已置頂為個人偏好或公司預設的規則不重複顯示 */}
      {visibleRuleSets
        .filter(r => r.id !== userPreferredRuleSetId && r.id !== companyDefaultRuleSet?.id)
        .map(ruleSet => {
          const uploaderInfo =
            !compact && ruleSet.created_by_name
              ? `${ruleSet.created_by_name}${ruleSet.created_by_department ? ` (${ruleSet.created_by_department})` : ''}`
              : '';
          const isBuiltIn = ruleSet.id < 0;
          return (
            <Select.Option key={ruleSet.id} value={ruleSet.id}>
              {ruleSet.is_default && !ruleSet.is_legaltech_library && (
                <StarFilled style={{ color: '#faad14', marginRight: 6 }} />
              )}
              {ruleSet.is_legaltech_library && (
                <img
                  src='/images/robot-logo.png'
                  alt=''
                  style={{
                    width: 16,
                    height: 16,
                    marginRight: 6,
                    verticalAlign: 'middle',
                    objectFit: 'contain',
                  }}
                />
              )}
              {ruleSet.rule_set_name}
              {!compact && (
                <span style={{ color: '#999', marginLeft: 8 }}>({ruleSet.rules_count} 條規則)</span>
              )}
              {ruleSet.is_default && (
                <span style={{ color: '#52c41a', marginLeft: 8, fontSize: 12 }}>預設</span>
              )}
              {!compact && uploaderInfo && !isBuiltIn && (
                <span style={{ color: '#1E88A8', marginLeft: 8, fontSize: 12 }}>
                  - {uploaderInfo}
                </span>
              )}
            </Select.Option>
          );
        })}
    </Select>
  );

  if (compact) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          fontSize: '14px',
          color: '#666',
        }}
      >
        📋 審閱規則：
        {ruleSelect}
        {historyRuleSetName && (
          <Tag icon={<BookOutlined />} color='geekblue'>
            {historyRuleSetName}
          </Tag>
        )}
      </div>
    );
  }

  return (
    <>
      <Card title={isComparisonPage ? '📋 比對規則設定' : '📋 審閱規則設定'}>
        <Space direction='vertical' style={{ width: '100%' }} size='middle'>
          {/* 上方：只保留選擇 + 上傳（移除重複按鈕） */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              選擇規則集：
            </label>

            <Space style={{ width: '100%' }} wrap>
              {/* 書籤：設定目前選取的規則為個人偏好（支援 AI 模式與律果規則庫）
                effectiveCode: AI=-1, 一般規則=rule_set_id, 未選=null */}
              {onSetUserPreferred &&
                (() => {
                  const activeVal = selectedValue !== undefined ? selectedValue : selectedRuleSetId;
                  const effectiveCode: number | null =
                    activeVal === 'ai_generated'
                      ? -1
                      : typeof activeVal === 'number'
                        ? activeVal
                        : null;
                  const isActive = effectiveCode !== null;
                  const isPreferred =
                    effectiveCode !== null && effectiveCode === userPreferredRuleSetId;
                  const isCompanyDefault =
                    effectiveCode !== null &&
                    effectiveCode > 0 &&
                    visibleRuleSets.find(r => r.id === effectiveCode)?.is_default === true;
                  return (
                    <Tooltip
                      title={
                        !isActive
                          ? '請先選擇規則集或模式'
                          : isPreferred
                            ? '取消我的偏好規則'
                            : '設為我的偏好規則'
                      }
                    >
                      <Button
                        type='text'
                        disabled={!isActive}
                        icon={
                          isPreferred ? (
                            <StarFilled style={{ color: '#1677ff', fontSize: 18 }} />
                          ) : isCompanyDefault ? (
                            <StarFilled style={{ color: '#faad14', fontSize: 18 }} />
                          ) : (
                            <StarOutlined
                              style={{ color: isActive ? '#8c8c8c' : '#d9d9d9', fontSize: 18 }}
                            />
                          )
                        }
                        onClick={() => {
                          if (effectiveCode === null) return;
                          onSetUserPreferred(isPreferred ? null : effectiveCode);
                        }}
                      />
                    </Tooltip>
                  );
                })()}
              {ruleSelect}

              {canUploadRules && (
                <Button icon={<UploadOutlined />} onClick={onUploadClick}>
                  {isComparisonPage ? '上傳文件' : '上傳規則'}
                </Button>
              )}

              {isSuperAdmin && !isComparisonPage && (
                <Button icon={<DatabaseOutlined />} onClick={() => setLegaltechModalOpen(true)}>
                  律果規則
                </Button>
              )}

              {isAdmin && (
                <Tooltip title='設定公司預設風險等級判斷標準'>
                  <Button icon={<SettingOutlined />} onClick={openRiskModal}>
                    風險預設
                  </Button>
                </Tooltip>
              )}

              {canUseValidationMode && !compact && (
                <Button
                  icon={<ExperimentOutlined />}
                  onClick={() => (onValidationModeToggle ?? (() => {}))(!validationMode)}
                  style={
                    validationMode
                      ? { background: '#4a5e3a', borderColor: '#4a5e3a', color: '#fff' }
                      : {}
                  }
                >
                  驗測模式
                </Button>
              )}
            </Space>
          </div>

          {/* 下方：管理清單（預覽/分享/設預設/刪除） */}
          {showManageBlock && listRuleSets.length > 0 && (
            <div
              style={{
                padding: '12px',
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid #f0f0f0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={() => setIsManageListExpanded(v => !v)}
              >
                <Space size={6}>
                  {isManageListExpanded ? (
                    <DownOutlined style={{ fontSize: 11, color: '#999' }} />
                  ) : (
                    <RightOutlined style={{ fontSize: 11, color: '#999' }} />
                  )}
                  <span style={{ fontWeight: 500, color: '#666' }}>已上傳的規則集：</span>
                  <span style={{ fontSize: 12, color: '#aaa' }}>({listRuleSets.length} 個)</span>
                </Space>
              </div>

              {isManageListExpanded && (
                <Space direction='vertical' style={{ width: '100%', marginTop: 8 }} size='small'>
                  {listRuleSets.map(ruleSet => {
                    const uploaderInfo = ruleSet.created_by_name
                      ? `${ruleSet.created_by_name}${
                          ruleSet.created_by_department ? ` (${ruleSet.created_by_department})` : ''
                        }`
                      : '';

                    const isSelected = selectedRuleSetId === ruleSet.id;

                    return (
                      <div
                        key={ruleSet.id}
                        onClick={() => {
                          if (
                            ruleSet.content_locked &&
                            !isAdmin &&
                            ruleSet.created_by !== Number(userId)
                          ) {
                            message.warning('此規則集內容已鎖定，僅管理員或創建者可查看');
                            return;
                          }
                          onPreviewClick(ruleSet);
                        }}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 12px',
                          background: isSelected ? '#e6f4f8' : '#fff',
                          borderRadius: 8,
                          border: isSelected ? '1px solid #91d5ff' : '1px solid #e8e8e8',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: 500 }}>{ruleSet.rule_set_name}</span>
                            <span style={{ color: '#999' }}>({ruleSet.rules_count} 條規則)</span>
                            {ruleSet.is_default && <Tag color='green'>預設</Tag>}
                            {ruleSet.content_locked && (
                              <Tag color='orange'>
                                <EyeInvisibleOutlined style={{ marginRight: 4 }} />
                                不公開
                              </Tag>
                            )}
                            {isSelected && <Tag color='blue'>目前選擇</Tag>}
                          </div>

                          {uploaderInfo && (
                            <div style={{ color: '#1E88A8', fontSize: 12, marginTop: 4 }}>
                              上傳者: {uploaderInfo}
                            </div>
                          )}
                        </div>

                        <Space size='small'>
                          {/* 眼睛按鈕：律果→預覽；Admin/創建者→切換鎖定；一般使用者→顯示狀態 */}
                          {ruleSet.is_legaltech_library ? (
                            <Tooltip title='預覽規則內容'>
                              <Button
                                type='text'
                                size='small'
                                icon={<EyeOutlined />}
                                onClick={e => {
                                  e.stopPropagation();
                                  onPreviewClick(ruleSet);
                                }}
                              />
                            </Tooltip>
                          ) : isAdmin || ruleSet.created_by === Number(userId) ? (
                            <Popconfirm
                              title={
                                ruleSet.content_locked
                                  ? '確定要解鎖此規則集內容嗎？'
                                  : '確定要鎖定此規則集內容嗎？'
                              }
                              description={
                                ruleSet.content_locked
                                  ? '解鎖後，有授權的使用者可查看規則內容。'
                                  : '鎖定後僅管理員或創建者可查看規則內容。'
                              }
                              okText='確定'
                              cancelText='取消'
                              onConfirm={async e => {
                                e?.stopPropagation();
                                await handleToggleLock(ruleSet);
                              }}
                              onCancel={e => e?.stopPropagation()}
                            >
                              <Tooltip
                                title={
                                  ruleSet.content_locked
                                    ? '內容已鎖定 — 點擊解鎖'
                                    : '內容公開中 — 點擊鎖定'
                                }
                              >
                                <Button
                                  type='text'
                                  size='small'
                                  icon={
                                    ruleSet.content_locked ? (
                                      <EyeInvisibleOutlined style={{ color: '#fa8c16' }} />
                                    ) : (
                                      <EyeOutlined style={{ color: '#8c8c8c' }} />
                                    )
                                  }
                                  onClick={e => e.stopPropagation()}
                                />
                              </Tooltip>
                            </Popconfirm>
                          ) : ruleSet.content_locked ? (
                            <Tooltip title='此規則集內容已鎖定，無法查看'>
                              <Button
                                type='text'
                                size='small'
                                disabled
                                icon={<EyeInvisibleOutlined />}
                                onClick={e => e.stopPropagation()}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title='預覽規則內容'>
                              <Button
                                type='text'
                                size='small'
                                icon={<EyeOutlined />}
                                onClick={e => {
                                  e.stopPropagation();
                                  onPreviewClick(ruleSet);
                                }}
                              />
                            </Tooltip>
                          )}

                          {canShareRules &&
                            !ruleSet.is_legaltech_library &&
                            (isAdmin || ruleSet.created_by === Number(userId)) && (
                              <Tooltip title='分享給其他部門'>
                                <Button
                                  type='text'
                                  size='small'
                                  icon={<TeamOutlined />}
                                  onClick={e => {
                                    e.stopPropagation();
                                    onShareClick(ruleSet);
                                  }}
                                />
                              </Tooltip>
                            )}

                          {isAdmin &&
                            !ruleSet.is_legaltech_library &&
                            (ruleSet.is_default ? (
                              <Popconfirm
                                title='取消預設規則集'
                                description='確定要取消此規則集的預設設定嗎？取消後將變為 private（非 admin 不可見）。'
                                okText='確定取消'
                                cancelText='保留'
                                onConfirm={e => {
                                  e?.stopPropagation();
                                  handleUnsetDefault(ruleSet);
                                }}
                                onCancel={e => e?.stopPropagation()}
                              >
                                <Tooltip title='點擊取消預設'>
                                  <Button
                                    type='text'
                                    size='small'
                                    icon={<StarFilled style={{ color: '#faad14' }} />}
                                    onClick={e => e.stopPropagation()}
                                  />
                                </Tooltip>
                              </Popconfirm>
                            ) : (
                              <Popconfirm
                                title='確定要設為預設規則集嗎？'
                                description='原本的預設規則會被取消，並依政策變為 private（非 admin 不可見）。'
                                okText='確定'
                                cancelText='取消'
                                onConfirm={e => {
                                  e?.stopPropagation();
                                  handleSetDefault(ruleSet);
                                }}
                                onCancel={e => e?.stopPropagation()}
                              >
                                <Tooltip title='設為預設'>
                                  <Button
                                    type='text'
                                    size='small'
                                    icon={<StarOutlined />}
                                    onClick={e => e.stopPropagation()}
                                  />
                                </Tooltip>
                              </Popconfirm>
                            ))}

                          {canDeleteRule(ruleSet) && (
                            <Popconfirm
                              title={`確定要刪除「${ruleSet.rule_set_name}」嗎？`}
                              description='刪除後將無法恢復'
                              okText='確定刪除'
                              cancelText='取消'
                              okButtonProps={{ danger: true }}
                              onConfirm={e => {
                                e?.stopPropagation();
                                handleDeleteRule(ruleSet.id, ruleSet.rule_set_name);
                              }}
                              onCancel={e => e?.stopPropagation()}
                            >
                              <Tooltip title='刪除此規則集'>
                                <Button
                                  type='text'
                                  danger
                                  size='small'
                                  icon={<DeleteOutlined />}
                                  onClick={e => e.stopPropagation()}
                                />
                              </Tooltip>
                            </Popconfirm>
                          )}
                        </Space>
                      </div>
                    );
                  })}
                </Space>
              )}
            </div>
          )}

          {selectedRuleSetId && (
            <div
              style={{
                padding: '12px',
                background: '#e6f4f8',
                borderRadius: '8px',
                fontSize: '14px',
              }}
            >
              ✓ 已選擇自定義規則集，審查時將使用該規則集
            </div>
          )}

          {!canUploadRules && (
            <div
              style={{
                padding: '12px',
                background: '#fff7e6',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#d48806',
              }}
            >
              💡 如需{isComparisonPage ? '上傳文件' : '上傳規則'}，請聯繫管理員開通權限
            </div>
          )}
        </Space>
      </Card>

      {/* 公司預設風險等級設定 Modal */}
      <Modal
        title={
          <Space>
            <SettingOutlined />
            預設風險等級
          </Space>
        }
        open={riskModalOpen}
        onCancel={() => setRiskModalOpen(false)}
        onOk={handleSaveRiskLevels}
        okText='儲存'
        cancelText='取消'
        confirmLoading={riskSaving}
        width={640}
        destroyOnHidden
      >
        {riskLoading ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Spin />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ color: '#888', fontSize: 13, marginBottom: 4 }}>
              此設定為全公司新建規則集時的預設風險判斷標準，各規則集可再個別覆寫。
            </div>
            {[
              { label: '高風險（high）', color: '#c0392b', value: riskHigh, setter: setRiskHigh },
              {
                label: '中風險（medium）',
                color: '#d4a017',
                value: riskMedium,
                setter: setRiskMedium,
              },
              { label: '低風險（low）', color: '#27ae60', value: riskLow, setter: setRiskLow },
            ].map(({ label, color, value, setter }) => (
              <div key={label}>
                <div style={{ fontSize: 13, fontWeight: 600, color, marginBottom: 6 }}>{label}</div>
                <Input.TextArea
                  value={value}
                  onChange={e => setter(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  style={{ fontSize: 13 }}
                />
              </div>
            ))}
          </div>
        )}
      </Modal>
      {/* 律果規則管理 Modal */}
      <LegaltechRulesModal open={legaltechModalOpen} onClose={() => setLegaltechModalOpen(false)} />
    </>
  );
};

export default RuleSelector;
